import React, { useRef } from "react";
import { Form, Button, Label } from "semantic-ui-react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnvironmentalRiskMatrixConesaform } from "../../../../api";
import { PROJECT_ACTIONS, ENVIRONMENTAL_FACTORS } from "../eiaCatalogs";
import { initialValues, validationSchema } from "./ConesaForm.form";
import "../EnvironmentalRiskMatrixForm/EnvironmentalRiskMatrixForm.scss";

const environmentalRiskMatrixConesaCtrl = new EnvironmentalRiskMatrixConesaform();

function toOptions(list) {
  return list.map((v) => ({ key: v, value: v, text: v }));
}

// Los 11 criterios de Conesa (1997), con sus escalas reales — nombres de
// opción en español (terminología propia del método), igual que en el
// prototipo. "Crítica"/"Crítico" en EX/MO son el tramo valor-máximo-normal+4
// de la guía original.
const CRITERIA = [
  { id: "IN", labelKey: "risk_matrix_conesa_in", options: [["Baja", 1], ["Media", 2], ["Alta", 4], ["Muy alta", 8], ["Total", 12]] },
  { id: "EX", labelKey: "risk_matrix_conesa_ex", options: [["Puntual", 1], ["Parcial", 2], ["Extensa", 4], ["Total", 8], ["Crítica", 12]] },
  { id: "MO", labelKey: "risk_matrix_conesa_mo", options: [["Largo plazo", 1], ["Medio plazo", 2], ["Inmediato", 4], ["Crítico", 8]] },
  { id: "PE", labelKey: "risk_matrix_conesa_pe", options: [["Fugaz", 1], ["Temporal", 2], ["Permanente", 4]] },
  { id: "RV", labelKey: "risk_matrix_conesa_rv", options: [["Corto plazo", 1], ["Medio plazo", 2], ["Irreversible", 4]] },
  { id: "SI", labelKey: "risk_matrix_conesa_si", options: [["Sin sinergia", 1], ["Sinérgico", 2], ["Muy sinérgico", 4]] },
  { id: "AC", labelKey: "risk_matrix_conesa_ac", options: [["Simple", 1], ["Acumulativo", 4]] },
  { id: "EF", labelKey: "risk_matrix_conesa_ef", options: [["Indirecto", 1], ["Directo", 4]] },
  { id: "PR", labelKey: "risk_matrix_conesa_pr", options: [["Irregular", 1], ["Periódico", 2], ["Continuo", 4]] },
  { id: "MC", labelKey: "risk_matrix_conesa_mc", options: [["Inmediata", 1], ["Medio plazo", 2], ["Mitigable", 4], ["Irrecuperable", 8]] },
];

function criterionOptions(options) {
  return options.map(([label, value]) => ({ key: `${label}-${value}`, value, text: `${label} (${value})` }));
}

function computePreview(values) {
  const g = (id) => Number(values[id]) || 0;
  const importance = 3 * g("IN") + 2 * g("EX") + g("MO") + g("PE") + g("RV") + g("SI") + g("AC") + g("EF") + g("PR") + g("MC");
  let significanceKey = "risk_matrix_conesa_sig_irrelevant";
  if (importance >= 75) significanceKey = "risk_matrix_conesa_sig_critical";
  else if (importance >= 50) significanceKey = "risk_matrix_conesa_sig_severe";
  else if (importance >= 25) significanceKey = "risk_matrix_conesa_sig_moderate";
  return { importance, significanceKey };
}

export function ConesaForm(props) {
  const { onClose, onReload, item, siteSelected } = props;

  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const submittingRef = useRef(false);

  const signOptions = [
    { key: "neg", value: "Negativo (−)", text: t("risk_matrix_gri_sign_negative") },
    { key: "pos", value: "Positivo (+)", text: t("risk_matrix_gri_sign_positive") },
  ];

  const formik = useFormik({
    initialValues: initialValues(item),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        if (!item) {
          const res = await environmentalRiskMatrixConesaCtrl.createEnvironmentalRiskMatrixConesa(accessToken, {
            ...formValue,
            site: user?.site || siteSelected,
          });
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        } else {
          const res = await environmentalRiskMatrixConesaCtrl.updateEnvironmentalRiskMatrixConesa(accessToken, item._id, formValue);
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        }
        setTimeout(() => {
          onReload?.();
          onClose?.();
        }, 800);
      } catch (error) {
        toast.error(error?.msg || error?.message || t("server_connection_error"));
      } finally {
        submittingRef.current = false;
      }
    },
  });

  const preview = computePreview(formik.values);
  const hasAnyCriterion = CRITERIA.some((c) => formik.values[c.id]);

  return (
    <Form className="risk-matrix-form" onSubmit={formik.handleSubmit}>
      <h3 className="risk-matrix-form__title">{item ? t("risk_matrix_edit_item") : t("risk_matrix_new_item")}</h3>
      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.title && !!formik.errors.title}>
          <label>{t("title")}</label>
          <Form.Input
            name="title"
            placeholder={t("risk_matrix_title_placeholder")}
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.title && formik.errors.title && (
            <Label basic color="red" pointing>{formik.errors.title}</Label>
          )}
        </Form.Field>

        <Form.Field>
          <label>{t("risk_matrix_gri_sign")}</label>
          <Form.Select
            options={signOptions}
            value={formik.values.sign}
            onChange={(_, { value }) => formik.setFieldValue("sign", value)}
          />
        </Form.Field>
      </div>

      <Form.Field>
        <label>{t("description")}</label>
        <Form.TextArea name="description" value={formik.values.description} onChange={formik.handleChange} />
      </Form.Field>

      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.projectAction && !!formik.errors.projectAction}>
          <label>{t("risk_matrix_eia_project_action")}</label>
          <Form.Dropdown
            selection
            search
            allowAdditions
            options={toOptions(PROJECT_ACTIONS)}
            value={formik.values.projectAction}
            placeholder={t("risk_matrix_eia_project_action_placeholder")}
            onAddItem={(_, { value }) => PROJECT_ACTIONS.push(value)}
            onChange={(_, { value }) => formik.setFieldValue("projectAction", value)}
            onBlur={() => formik.setFieldTouched("projectAction", true)}
          />
          {formik.touched.projectAction && formik.errors.projectAction && (
            <Label basic color="red" pointing>{formik.errors.projectAction}</Label>
          )}
        </Form.Field>

        <Form.Field error={formik.touched.environmentalFactor && !!formik.errors.environmentalFactor}>
          <label>{t("risk_matrix_eia_environmental_factor")}</label>
          <Form.Dropdown
            selection
            search
            allowAdditions
            options={toOptions(ENVIRONMENTAL_FACTORS)}
            value={formik.values.environmentalFactor}
            placeholder={t("risk_matrix_eia_environmental_factor_placeholder")}
            onAddItem={(_, { value }) => ENVIRONMENTAL_FACTORS.push(value)}
            onChange={(_, { value }) => formik.setFieldValue("environmentalFactor", value)}
            onBlur={() => formik.setFieldTouched("environmentalFactor", true)}
          />
          {formik.touched.environmentalFactor && formik.errors.environmentalFactor && (
            <Label basic color="red" pointing>{formik.errors.environmentalFactor}</Label>
          )}
        </Form.Field>
      </div>

      {CRITERIA.reduce((rows, criterion, i) => {
        if (i % 2 === 0) rows.push([criterion]);
        else rows[rows.length - 1].push(criterion);
        return rows;
      }, []).map((pair, i) => (
        <div className="risk-matrix-form__row" key={i}>
          {pair.map((c) => (
            <Form.Field key={c.id} error={formik.touched[c.id] && !!formik.errors[c.id]}>
              <label>{t(c.labelKey)}</label>
              <Form.Select
                options={criterionOptions(c.options)}
                value={formik.values[c.id]}
                onChange={(_, { value }) => formik.setFieldValue(c.id, value)}
                onBlur={() => formik.setFieldTouched(c.id, true)}
              />
              {formik.touched[c.id] && formik.errors[c.id] && (
                <Label basic color="red" pointing>{formik.errors[c.id]}</Label>
              )}
            </Form.Field>
          ))}
        </div>
      ))}

      {hasAnyCriterion && (
        <div className="risk-matrix-form__preview">
          <span>{t("risk_matrix_conesa_importance")}: <b>{preview.importance}</b></span>
          <span className={`risk-matrix-form__preview-sig${preview.importance >= 50 ? " is-significant" : ""}`}>
            {t(preview.significanceKey)}
          </span>
        </div>
      )}

      <Form.Field>
        <label>{t("risk_matrix_controls")}</label>
        <Form.TextArea name="controls" value={formik.values.controls} onChange={formik.handleChange} />
      </Form.Field>

      <Form.Field>
        <label>{t("risk_matrix_action")}</label>
        <Form.TextArea name="action" value={formik.values.action} onChange={formik.handleChange} />
      </Form.Field>

      <div className="risk-matrix-form__actions">
        <Button type="submit" primary loading={formik.isSubmitting} disabled={formik.isSubmitting}>
          {!item ? t("save") : t("update")}
        </Button>
        <Button type="button" onClick={() => onClose?.()}>
          {t("cancel")}
        </Button>
      </div>
    </Form>
  );
}
